import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import MappingAddForm from '../components/snoop/MappingAddForm'
import {
  fetchPLineMapping,
  upsertPLineMapping,
  deletePLineMapping,
} from '../api/luminateClient'
import { PLACEHOLDER_P_LINE_MAPPING } from '../utils/snoop/placeholderColumns'

const PARTIAL_OPTIONS = [
  { value: 'false', label: 'Exact match' },
  { value: 'true', label: 'Partial (substring)' },
]

function partialToBool(v) {
  if (typeof v === 'boolean') return v
  const s = String(v ?? '').trim().toLowerCase()
  return s === 'true' || s === '1' || s === 'yes'
}

export default function PLineMappingPage() {
  const [reloadNonce, setReloadNonce] = useState(0)

  // reloadNonce is intentionally unused inside the callback: bumping it must
  // produce a new fetchData identity so SummaryDashboard refetches after an edit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(() => fetchPLineMapping(), [reloadNonce])
  const bump = useCallback(() => setReloadNonce((n) => n + 1), [])

  const editableStatus = useMemo(
    () => ({
      Partial: {
        target: 'p_line_mapping',
        uidColumn: 'Text',
        options: PARTIAL_OPTIONS,
        onSave: async ({ status, uid }) => {
          await upsertPLineMapping({
            text: uid,
            partial: partialToBool(status),
          })
        },
      },
    }),
    [],
  )

  const onDeleteRow = useCallback(async (row) => {
    await deletePLineMapping({ text: row?.Text })
    bump()
  }, [bump])

  const addForm = useMemo(
    () => (
      <MappingAddForm
        title="Add / update ignore pattern"
        submitLabel="Save pattern"
        fields={[
          {
            name: 'text',
            label: 'Text',
            placeholder: 'Label / imprint text…',
            required: true,
          },
          {
            name: 'partial',
            label: 'Partial substring match',
            type: 'checkbox',
          },
        ]}
        onSubmit={async (values) => {
          await upsertPLineMapping({
            text: values.text,
            partial: Boolean(values.partial),
          })
          bump()
        }}
      />
    ),
    [bump],
  )

  return (
    <SummaryDashboard
      title="P-line Ignore Labels"
      subtitle="Ignore labels · Exact / Partial"
      fetchData={fetchData}
      defaultSort={[
        { column: 'Partial', direction: 'asc' },
        { column: 'Text', direction: 'asc' },
      ]}
      searchPlaceholder="Search ignore text…"
      editableStatus={editableStatus}
      onDeleteRow={onDeleteRow}
      addForm={addForm}
      layout="mapping"
      placeholderColumns={PLACEHOLDER_P_LINE_MAPPING}
      tableKey={`pline-${reloadNonce}`}
    />
  )
}
